import React from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '../../ui/icon';
import {
  faDownload,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import './sales-copilot-modal.css';

interface SalesCopilotModalProps {
  /** Whether the modal is currently open */
  open: boolean;
  /** Callback when the modal open state changes */
  onOpenChange: (open: boolean) => void;
}

interface CopilotOptionCardProps {
  /** Title of the option - can include JSX for line breaks */
  title: React.ReactNode;
  /** Button label */
  buttonLabel: string;
  /** URL to navigate to */
  href: string;
  /** Whether the button has a primary (pink/rose) style */
  isPrimary?: boolean;
  /** ID for the link element */
  id?: string;
  /** ID for the card container */
  cardId?: string;
}

function CopilotOptionCard({
  title,
  buttonLabel,
  href,
  isPrimary = false,
  id,
  cardId,
}: CopilotOptionCardProps) {
  const buttonClass = isPrimary
    ? 'copilot-card__button copilot-card__button--primary'
    : 'copilot-card__button copilot-card__button--outline';

  return (
    <div className="copilot-card" id={cardId}>
      <h3 className="copilot-card__title">{title}</h3>
      <div className="copilot-card__action">
        <a
          href={href}
          id={id}
          target="_blank"
          rel="noopener noreferrer"
          className="copilot-card__link"
        >
          <span className={buttonClass}>
            <Icon
              icon={isPrimary ? faDownload : faArrowUpRightFromSquare}
              className="copilot-card__button-icon"
            />
            {buttonLabel}
          </span>
        </a>
      </div>
    </div>
  );
}

/**
 * Get the Sales Copilot Web URL
 * Returns staging URL for non-production environments, production for hginsights.com
 * Safe for SSR - returns staging during server-side rendering
 */
function getSalesCopilotWebUrl(): string {
  // During SSR, window is undefined - default to staging
  if (typeof window === 'undefined' || !window.location) {
    return 'https://msi-staging.hginsights.info/';
  }
  const isProduction = ['hginsights.com', 'madkudu.com'].some((domain) =>
    window.location.origin.includes(domain)
  );
  return isProduction
    ? 'https://msi.hginsights.com/'
    : 'https://msi-staging.hginsights.info/';
}

export function SalesCopilotModal({
  open,
  onOpenChange,
}: SalesCopilotModalProps) {
  const chromeExtensionUrl =
    'https://chromewebstore.google.com/detail/madkudu-copilot/jikgjincjapiebekopikcefbkeaimegj';
  const salesforceInstallUrl =
    'https://help.madkudu.com/docs/madkudu-salesforce-package';
  const salesCopilotWebUrl = getSalesCopilotWebUrl();

  return (
    <Modal
      show={open}
      onHide={() => onOpenChange(false)}
      size="lg"
      centered
      dialogClassName="copilot-modal"
      id="sales-copilot-modal"
    >
      <Modal.Header closeButton id="sales-copilot-modal-close">
        <Modal.Title
          className="copilot-modal__title"
          id="sales-copilot-modal-title"
        >
          Sales Copilot
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="sales-copilot-modal-body">
        <div
          className="copilot-modal__description"
          id="sales-copilot-modal-description"
        >
          <p className="copilot-modal__text">
            Give your sales team the context they need to personalize outreach,
            and get more meetings booked, directly in Salesforce.
          </p>
          <p className="copilot-modal__text">
            Sales Copilot surfaces relevant insights in your CRM – such as{' '}
            <span className="copilot-modal__highlight">job openings</span>,{' '}
            <span className="copilot-modal__highlight">tech stack</span>, and{' '}
            <span className="copilot-modal__highlight">recent activity</span>{' '}
            within your product or on your website.
          </p>
        </div>

        <div className="copilot-modal__cards" id="sales-copilot-modal-cards">
          <CopilotOptionCard
            title={
              <>
                Sales Copilot
                <br />
                Chrome Extension
              </>
            }
            buttonLabel="Install Extension"
            href={chromeExtensionUrl}
            isPrimary
            id="sales-copilot-modal-chrome-extension"
            cardId="sales-copilot-modal-chrome-extension-card"
          />
          <CopilotOptionCard
            title="Sales Copilot in Salesforce"
            buttonLabel="Install for Salesforce"
            href={salesforceInstallUrl}
            id="sales-copilot-modal-salesforce"
            cardId="sales-copilot-modal-salesforce-card"
          />
          <CopilotOptionCard
            title="Sales Copilot Web"
            buttonLabel="Go to Sales Copilot"
            href={salesCopilotWebUrl}
            id="sales-copilot-modal-web"
            cardId="sales-copilot-modal-web-card"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}
